import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Newspaper, Trophy, Target, Gift } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const priorityColor = (p) => {
  if (p === 'urgent' || p === 'high') return 'bg-rose-100 text-rose-700';
  if (p === 'medium') return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-600';
};

export const NewsChallengesWidget = () => {
  const [news, setNews] = useState([]);
  const [challenges, setChallenges] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [nr, cr] = await Promise.all([
          fetch(`${API}/news`, { credentials: 'include' }),
          fetch(`${API}/challenges`, { credentials: 'include' }),
        ]);
        if (nr.ok) setNews(await nr.json());
        if (cr.ok) setChallenges(await cr.json());
      } catch (e) {
        console.error('Failed to load news/challenges:', e);
      }
    })();
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="news-challenges-widget">
      {/* Actualités */}
      <Card className="border-0 shadow-sm" data-testid="dashboard-news-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-indigo-100">
              <Newspaper className="w-4 h-4 text-indigo-600" strokeWidth={1.75} />
            </div>
            <div>
              <CardTitle className="font-[Manrope] text-lg">Actualités récentes</CardTitle>
              <CardDescription>Dernières annonces de votre périmètre</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {news.slice(0, 5).map((n) => (
              <div
                key={n.news_id}
                className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-900"
                data-testid={`dashboard-news-${n.news_id}`}
              >
                <div className="w-2 h-2 mt-2 rounded-full bg-indigo-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">{n.title}</p>
                    {n.is_important && (
                      <Badge className="bg-rose-100 text-rose-700 text-[10px]">Important</Badge>
                    )}
                    {n.priority && n.priority !== 'normal' && (
                      <Badge className={`${priorityColor(n.priority)} text-[10px]`}>{n.priority}</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.content}</p>
                </div>
              </div>
            ))}
            {news.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-6">Aucune actualité</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Challenges */}
      <Card className="border-0 shadow-sm" data-testid="dashboard-challenges-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-amber-100">
              <Trophy className="w-4 h-4 text-amber-600" strokeWidth={1.75} />
            </div>
            <div>
              <CardTitle className="font-[Manrope] text-lg">Challenges en cours</CardTitle>
              <CardDescription>Objectifs et récompenses actifs</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {challenges.slice(0, 5).map((c) => (
              <div
                key={c.challenge_id}
                className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900"
                data-testid={`dashboard-challenge-${c.challenge_id}`}
              >
                <p className="text-sm font-medium text-slate-900 dark:text-white">{c.title}</p>
                {c.description && (
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{c.description}</p>
                )}
                <div className="flex items-center gap-4 mt-2">
                  <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                    <Target className="w-3 h-3 text-indigo-500" />
                    {(c.target || 0).toLocaleString('fr-FR')} €
                  </span>
                  {c.reward && (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                      <Gift className="w-3 h-3" />
                      {c.reward}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {challenges.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-6">Aucun challenge en cours</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NewsChallengesWidget;
